import React from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';

const AAticleBusinessControlling = () => {
  const sections = [
    {
      id: 1,
      title: "The Strategic Value of Business Controlling",
      text: `In today's fast-paced and competitive business environment, staying ahead requires more than just intuition and hard work. Businesses need clear, data-driven insights and strategic guidance to make informed decisions. This is where the role of a business controller becomes indispensable.`,
      explanation: "Understanding why business controlling matters in modern organizations"
    },
    {
      id: 2,
      title: "What Is Business Controlling?",
      text: `Business controlling is not just about keeping the books or ensuring compliance. It is a strategic function that connects financial data, operational performance, and long-term goals. Business controllers are the bridge between numbers and actionable insights. They provide the clarity and foresight that organizations need to navigate uncertainty and achieve sustainable growth.`,
      explanation: "Defining the core purpose of business controlling"
    },
    {
      id: 3,
      title: "How Business Controlling Adds Value Through Four Channels",
      text: `Through financial transparency and discipline, the Business controller ensure that financial data is accurate, up-to-date, and meaningful. By maintaining transparency, they help leaders understand the true financial health of the organization. This enables better resource allocation and prevents costly inefficiencies.

Through strategic decisions, a business controller translates raw data into strategic insights. For example, by analyzing cost structures and profitability, they can identify areas for improvement and recommend adjustments. This guidance ensures that decisions are not only data-backed but also aligned with the company's long-term objectives.

By manageing risk. Every business faces risks, whether it's market volatility, regulatory changes, or operational inefficiencies. Business controllers help identify these risks early by monitoring key performance indicators (KPIs) and performing scenario analyses. Proactive risk management safeguards the organization's stability and reputation.

Through performance monitoring and optimization, the business controller design and oversee dashboards that track critical KPIs, offering a real-time view of the company's performance. By highlighting deviations from expected results, they ensure swift corrective actions, keeping the business on track.

By opening the company through cross-functional collaboration. The business controller work closely with different departments, fostering collaboration between finance, operations, sales, and more. This holistic view ensures that all functions are aligned toward the company's goals, breaking down silos and enhancing efficiency.`,
      explanation: "Understanding the key ways business controllers create value"
    },
    {
      id: 4,
      title: "Why Businesses Need Business Controllers",
      text: `The modern business landscape is increasingly complex. From navigating digital transformation to managing global supply chains, organizations face a multitude of challenges. Business controllers bring order to this complexity by:

1) Offering a clear roadmap based on data analysis
2) Providing insights to improve margins and manage costs effectively
3) Helping the organization remain agile in response to market changes

Moreover, as enterprises grow, the need for robust financial planning and analysis becomes even more critical. A business controller's expertise ensures that growth is not only achieved but also sustainable.`,
      explanation: "The critical role of business controllers in modern organizations"
    },
    {
      id: 5,
      title: "Conclusion",
      text: `The role of a business controller is much more than a behind-the-scenes function. It is a strategic partnership that drives business success. By ensuring financial clarity, supporting decision-making, and fostering collaboration, business controllers create value that resonates across the entire organization. For any enterprise aiming to thrive in the modern era, investing in strong business controlling capabilities is not just an option—it is a necessity.`,
      explanation: "Summarizing the importance of business controlling"
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          {/* Add Table of Contents */}
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-16">
            {sections.map(({ id, title, text, explanation }, index) => (
              <motion.div 
                key={id}
                id={`section-${id}`}  // Add this line for scroll targeting
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: index * 0.3 }}
                className="flex flex-col gap-6"
              >
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>

          {/* Add Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className="text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110"
              style={{ background: 'rgb(1, 50, 32)' }}
            >
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AAticleBusinessControlling;